import { MegaphoneSimple as PageHeadingIcon } from '@phosphor-icons/react';
import StartupPageTitle from '../components/StartupPageTitle';
/**
 * AgencyProfile.jsx — public(-ish) agency profile page.
 * Route: /startup/studio/agency/:agencyId
 *
 *   - Hero: name, tagline, verified badge, pricing, match %
 *   - Stats row: turnaround, reviews, sector focus, stage focus
 *   - Portfolio gallery (lightbox on click)
 *   - Recent reviews
 *   - Case studies
 *   - Sticky CTA: Book intro call (opens IntroCallModal)
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import {
  ArrowLeft, ShieldCheck, Star, Spinner, Calendar, X, Check, Clock,
  Quotes, ArrowRight,
} from '@phosphor-icons/react';

const AgencyProfile = ({ user }) => {
  const { agencyId } = useParams();
  const navigate = useNavigate();
  const accent = user?.role === 'startup' ? '#10b981' : '#6366f1';
  const [agency, setAgency] = useState(null);
  const [match, setMatch] = useState(null);
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookOpen, setBookOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/agencies/${agencyId}`),
      api.get('/agencies/match-scores').catch(() => ({ data: { scores: {} } })),
      api.get(`/agencies/${agencyId}/bundles`).catch(() => ({ data: { bundles: [] } })),
    ]).then(([a, ms, b]) => {
      setAgency(a.data.agency);
      setMatch(ms.data.scores?.[agencyId]);
      setBundles(b.data.bundles || []);
    }).catch((e) => { toast.error(e.response?.data?.detail || 'Failed to load'); }).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [agencyId]);

  if (loading) return <div className="p-12 text-sm text-text-muted flex items-center gap-2"><Spinner size={14} className="animate-spin" /> Loading agency…</div>;
  if (!agency) return <div className="p-12 text-sm text-text-muted">Agency not found.</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1280px] mx-auto pb-32" data-testid="studio-agency-profile">
      <button onClick={() => navigate(-1)} className="text-xs text-text-muted hover:text-text-primary inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={11} weight="bold" /> Back to marketplace
      </button>

      {/* HERO */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 mb-8">
        <div>
          <div className="flex items-center gap-3 mb-3">
            {agency.verified && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>
                <ShieldCheck size={11} weight="fill" /> Verified
              </span>
            )}
            {match && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded" style={{ backgroundColor: `${accent}1a`, color: accent }} data-testid="studio-profile-match">
                {match.score}% match
              </span>
            )}
          </div>
          <StartupPageTitle icon={PageHeadingIcon} className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-text-primary leading-[1.05] mb-3" style={{ letterSpacing: '-0.02em' }}>
            {agency.name}
          </StartupPageTitle>
          <p className="text-base text-text-secondary leading-relaxed mb-4">{agency.tagline}</p>
          <p className="text-sm text-text-secondary leading-relaxed">{agency.description}</p>
        </div>
        {/* CTA card */}
        <div className="bg-surface border border-border rounded-xl p-5 self-start sticky top-4">
          <div className="text-2xl font-heading font-bold text-text-primary mb-1" style={{ letterSpacing: '-0.02em' }}>{agency.pricing_display || '—'}</div>
          <div className="text-[11px] text-text-muted mb-4">Starting band · negotiated in SOW</div>
          {agency.review_count > 0 && (
            <div className="flex items-center gap-1.5 mb-4 text-sm">
              <Star size={13} weight="fill" className="text-amber-500" />
              <span className="font-mono font-bold text-text-primary">{agency.review_avg?.toFixed(1)}</span>
              <span className="text-text-muted text-[12px]">· {agency.review_count} verified review{agency.review_count === 1 ? '' : 's'}</span>
            </div>
          )}
          <div className="text-[11px] text-text-muted inline-flex items-center gap-1 mb-4"><Clock size={11} weight="bold" /> {agency.turnaround_days ? `${agency.turnaround_days}-day avg turnaround` : 'On request'}</div>
          <button
            onClick={() => setBookOpen(true)}
            data-testid="studio-profile-book-intro"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
            style={{ backgroundColor: accent }}
          >
            <Calendar size={14} weight="bold" /> Book intro call
          </button>
          {user?.role === 'startup' && (
            <button
              onClick={async () => {
                try {
                  const r = await api.post('/engagements', { agency_id: agencyId });
                  toast.success('Engagement started');
                  navigate(`/startup/marketing/engagements/${r.data.engagement.id}`);
                } catch (e) {
                  if (e.response?.data?.detail?.includes('already have an active')) {
                    toast.error(e.response.data.detail);
                  } else {
                    toast.error(e.response?.data?.detail || 'Failed to start engagement');
                  }
                }
              }}
              data-testid="studio-profile-start-engagement"
              className="w-full mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold border-2 transition-colors hover:bg-text-primary/5"
              style={{ borderColor: accent, color: accent }}
            >
              Start engagement <ArrowRight size={11} weight="bold" />
            </button>
          )}
          <p className="text-[10px] text-text-muted text-center mt-2">No commitment. Just a 30-min chat.</p>
        </div>
      </div>

      {/* Match reasons */}
      {match && (match.reasons || []).length > 0 && (
        <div className="border-l-2 rounded-r-lg bg-surface px-5 py-4 mb-8" style={{ borderLeftColor: accent }} data-testid="studio-profile-match-reasons">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-text-muted mb-2">Why this is a {match.score}% match for you</div>
          <ul className="space-y-1.5">
            {match.reasons.map((r, i) => (
              <li key={i} className="text-sm text-text-secondary flex items-start gap-2">
                <Check size={11} weight="bold" className="text-success flex-shrink-0 mt-1.5" /><span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatBlock label="Sectors" value={(agency.sector_focus || []).join(' · ') || '—'} />
        <StatBlock label="Stages" value={(agency.stage_focus || []).map(s => s.replace(/_/g, ' ')).join(' · ') || '—'} />
        <StatBlock label="Services" value={`${(agency.service_tags || []).length} on offer`} />
        <StatBlock label="Languages" value={(agency.languages || []).join(', ') || 'English'} />
      </div>

      {/* Services */}
      <div className="mb-8">
        <h2 className="text-xl font-heading font-bold text-text-primary mb-3" style={{ letterSpacing: '-0.02em' }}>Services offered</h2>
        <div className="flex flex-wrap gap-2" data-testid="studio-profile-services">
          {(agency.service_tags || []).map((s) => (
            <span key={s} className="text-xs px-3 py-1.5 rounded-md bg-surface border border-border text-text-primary capitalize">
              {s.replace(/_/g, ' ')}
            </span>
          ))}
        </div>
      </div>

      {/* Bundles / Rate cards */}
      {bundles.length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-heading font-bold text-text-primary mb-3" style={{ letterSpacing: '-0.02em' }}>Rate cards &amp; bundles</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="studio-profile-bundles">
            {bundles.map((b) => (
              <div key={b.id} className="bg-surface border border-border rounded-xl p-4 flex flex-col" data-testid={`studio-profile-bundle-${b.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-sm font-heading font-bold text-text-primary flex-1">{b.title}</h3>
                  {b.is_featured && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded" style={{ backgroundColor: `${accent}1a`, color: accent }}>Featured</span>}
                </div>
                <div className="text-2xl font-mono font-bold text-text-primary mb-1">{b.currency} {(b.price || 0).toLocaleString('en-IN')}</div>
                <div className="text-[11px] text-text-muted mb-3">{b.duration_label || 'one-time'}</div>
                {b.description && <p className="text-xs text-text-secondary mb-3">{b.description}</p>}
                {(b.deliverables || []).length > 0 && (
                  <ul className="space-y-0.5 text-[11px] text-text-secondary mt-auto">
                    {b.deliverables.map((d, i) => (
                      <li key={i} className="inline-flex items-start gap-1.5"><span className="text-success mt-0.5">✓</span> {d}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Portfolio */}
      {(agency.portfolio || []).length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-heading font-bold text-text-primary mb-3" style={{ letterSpacing: '-0.02em' }}>Portfolio</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="studio-profile-portfolio">
            {agency.portfolio.map((p) => (
              <button
                key={p.id}
                onClick={() => setLightbox(p)}
                data-testid={`studio-profile-portfolio-${p.id}`}
                className="bg-surface border border-border rounded-lg p-4 text-left hover:border-text-primary transition-colors"
              >
                <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: accent }}>{(p.service || '').replace(/_/g, ' ')}</div>
                <div className="text-sm font-medium text-text-primary mb-1">{p.title}</div>
                <div className="text-[11px] text-text-secondary line-clamp-2">{p.outcome}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Case studies */}
      {(agency.case_studies || []).length > 0 && (
        <div className="mb-8">
          <h2 className="text-xl font-heading font-bold text-text-primary mb-3" style={{ letterSpacing: '-0.02em' }}>Case studies</h2>
          <div className="space-y-3" data-testid="studio-profile-case-studies">
            {agency.case_studies.map((cs) => (
              <div key={cs.id} className="bg-surface border border-border rounded-lg p-4">
                <h3 className="text-base font-heading font-bold text-text-primary mb-2">{cs.title}</h3>
                <p className="text-sm text-text-secondary leading-relaxed mb-3">{cs.summary}</p>
                <div className="flex flex-wrap gap-3 text-[11px]">
                  {Object.entries(cs.outcome_metrics || {}).map(([k, v]) => (
                    <span key={k} className="px-2 py-1 rounded bg-background border border-border text-text-secondary">
                      <span className="text-text-muted">{k.replace(/_/g, ' ')}:</span> <span className="text-text-primary font-mono font-semibold">{String(v)}</span>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reviews */}
      <div>
        <h2 className="text-xl font-heading font-bold text-text-primary mb-3" style={{ letterSpacing: '-0.02em' }}>Verified founder reviews</h2>
        {(agency.recent_reviews || []).length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-text-muted">No reviews yet — be the first to leave one after your engagement.</div>
        ) : (
          <div className="space-y-3" data-testid="studio-profile-reviews">
            {agency.recent_reviews.map((r) => (
              <div key={r.id} className="bg-surface border border-border rounded-lg p-4">
                <div className="flex items-start justify-between mb-2 gap-3">
                  <div>
                    <div className="text-sm font-bold text-text-primary">{r.reviewer_name} <span className="text-text-muted font-normal">· {r.startup_name}{r.funding_stage ? ` · ${r.funding_stage}` : ''}</span></div>
                    <div className="text-[11px] text-text-muted mt-0.5">{r.engagement_months}-month engagement · {r.approved_deliverables_count} approved deliverables</div>
                  </div>
                  <div className="flex items-center gap-1 text-amber-500">
                    {[...Array(r.overall_rating || 0)].map((_, i) => <Star key={i} size={11} weight="fill" />)}
                  </div>
                </div>
                <Quotes size={13} weight="fill" className="text-text-muted/30 mb-1" />
                <p className="text-sm text-text-secondary leading-relaxed">{r.comment}</p>
                <div className="flex flex-wrap gap-3 text-[10px] mt-3 pt-3 border-t border-border">
                  {Object.entries(r.sub_ratings || {}).map(([k, v]) => (
                    <span key={k} className="uppercase tracking-wider text-text-muted">{k}: <span className="text-text-primary font-mono font-semibold">{v}/5</span></span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {bookOpen && (
        <IntroCallModal
          accent={accent}
          agency={agency}
          onClose={() => setBookOpen(false)}
          onBooked={() => { setBookOpen(false); toast.success(`Intro call requested — ${agency.name} will confirm shortly.`); }}
        />
      )}

      {lightbox && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur z-50 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="studio-profile-lightbox">
          <div className="bg-surface border border-border rounded-xl p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-3 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: accent }}>{(lightbox.service || '').replace(/_/g, ' ')}</div>
                <h3 className="text-lg font-heading font-bold text-text-primary">{lightbox.title}</h3>
              </div>
              <button onClick={() => setLightbox(null)} className="text-text-muted hover:text-text-primary"><X size={18} weight="bold" /></button>
            </div>
            <p className="text-sm text-text-secondary leading-relaxed">{lightbox.outcome}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const StatBlock = ({ label, value }) => (
  <div className="border-l-2 border-border pl-3 py-1">
    <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-text-muted mb-1">{label}</div>
    <div className="text-sm text-text-primary font-medium leading-tight">{value}</div>
  </div>
);

const IntroCallModal = ({ accent, agency, onClose, onBooked }) => {
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
  const [slot, setSlot] = useState(todayIso);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!slot) { toast.error('Pick a slot'); return; }
    setBusy(true);
    try {
      await api.post('/intro-calls', {
        agency_id: agency.id,
        proposed_slot: new Date(slot).toISOString(),
        founder_notes: notes,
      });
      onBooked();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="studio-intro-call-modal">
      <div className="bg-surface border border-border rounded-xl p-6 w-[500px] max-w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4 gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: accent }}>Book a 30-min intro call</div>
            <h3 className="font-heading font-bold text-text-primary text-lg">{agency.name}</h3>
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={18} weight="bold" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">Preferred slot</label>
            <input
              type="datetime-local"
              value={slot}
              onChange={(e) => setSlot(e.target.value)}
              data-testid="studio-intro-call-slot"
              min={todayIso}
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-text-primary"
            />
            <div className="text-[10px] text-text-muted mt-1">The agency will confirm or propose an alternative within 1 business day.</div>
          </div>
          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">What do you want to talk about? <span className="text-text-muted/60 normal-case font-normal">(optional)</span></label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder="e.g. We're scaling our LinkedIn presence to feed our seed round. Need a content engine that survives a fundraise."
              data-testid="studio-intro-call-notes"
              className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-border rounded-lg text-text-secondary hover:text-text-primary">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            data-testid="studio-intro-call-submit"
            className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ backgroundColor: accent }}
          >
            {busy ? <Spinner size={14} className="animate-spin" /> : <Calendar size={14} weight="bold" />} Request call <ArrowRight size={11} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgencyProfile;
